import React, { useState, useMemo } from "react";
import moment from "moment"; // Assuming moment is used for date logic
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import ALoader from "views/pages/manage/common/a-loader";
import ChequeDetails from "./ChequeDetails";
import { useUpdateChequeStatus } from "../hooks/useUpdateChequeStatus";
import "../styles/depositChequeForm.scss";
import { getFormatedDate } from "views/pages/manage/unAssigned/concessionApproval/common";
import useStudentDetails from "modules/paymentManagement/cheque-management/hooks/useStudentDetails";
import { failureToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import { useBuPanLevelFeeConfiguration } from "../hooks/useFetchConfig";

const inputHeight = "44px";

// Expanded Dropdowns
const CancelReasons = [
  { label: "Left", value: "LEFT" },
  { label: "Transfer Out", value: "TRANSFER_OUT" },
  { label: "Payment Mode Change", value: "PAYMENT_MODE_CHANGE" },
  { label: "Others", value: "OTHERS" },
];

const BounceReasons = [
  { label: "Insufficient Fund", value: "INSUFFICIENT_FUND" },
  { label: "No Account Number", value: "NO_ACCOUNT_NUMBER" },
  { label: "Over Writing on Cheque", value: "OVER_WRITING" },
  { label: "Signature Mismatch", value: "SIGNATURE_MISMATCH" },
  { label: "Soiled Cheque", value: "SOILED_CHEQUE" },
  { label: "Stop Payment", value: "STOP_PAYMENT" },
  { label: "Other", value: "OTHERS" },
];

const notProgramAllowedStatus = [ "IBTR","CRCG","STCG","DISC"]


const CancelChequeForm = ({
  row,
  refreshList = () => {},
  mode = "CANCEL", // "CANCEL" or "BOUNCE"
  config = {
    bounceDayLimit: 53,
    restrictedStatuses: ["IBTR", "CRCG", "STCG", "DISC"],
  },
  state,
  setState
}) => {
  const [form, setForm] = useState({
    reason: null,
    remarks: "",
  });

  const { updateChequeStatus, isLoading } = useUpdateChequeStatus({
    isChequeDateExtension: false,
  });
  const { studentDetails, loading: studentLoading } = useStudentDetails(
    row?.applicationId
  );

  const {config:cashControlConfig,loading:configLoading } = useBuPanLevelFeeConfiguration(studentDetails); 
  const bounceConfig = cashControlConfig?.bounceDays;

  // 1. Validation Logic
  const validation = useMemo(() => {
    if (mode !== "BOUNCE") return { isValid: true, error: null };

    // Time-based restriction (3.1)
    const depositDate = moment(row.depositDate); // Ensure row has depositDate
    const daysSinceDeposit = moment().diff(depositDate, "days");
    if (cashControlConfig && daysSinceDeposit > cashControlConfig?.bounceDays) {
      return {
        isValid: false,
        error: `You can not bounce the cheque  after ${cashControlConfig?.bounceDays} days of deposit. Please take finance approval and raise the ticket.`,
      };
    }

    if (config.restrictedStatuses.includes(row.applicationStatus)) {
      return {
        isValid: false,
        error: `Cheque Bounce is not allowed for the application Status ${row.applicationStatus}`,
      };
    }

    return { isValid: true, error: null };
  }, [mode, row, config, cashControlConfig]);

  const rowFormHandler = (value, key) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onRefreshList = (response) => {
    refreshList(response, row, mode);
  };

  const handleConfirm = () => {
    if(!bounceConfig && mode === "BOUNCE"){
      failureToast(`Bounce day limit configuration is missing. Please contact administrator.`);
      return;
    }

     if(notProgramAllowedStatus.includes(studentDetails?.programAction)){
        failureToast(`Cheque ${mode} is not allowed as student Application status is ${studentDetails?.programAction}`);
        return;
     }
    const isBounce = mode === "BOUNCE";

    const requestPayload = {
      psid: row.psid,
      referenceNumber: row.referenceNumber,
      updateStatus: isBounce ? "BOUNCED" : "CANCELLED",
      remarks:
        form.reason?.value === "OTHERS" ? form.remarks : form.reason?.value,
    };

    updateChequeStatus(requestPayload, onRefreshList,()=>{}, state, setState);
  };

  const isOtherSelected = form.reason?.value === "OTHERS";
  const dropdownItems = mode === "BOUNCE" ? BounceReasons : CancelReasons;
  const primaryColor = mode === "BOUNCE" ? "#DC2804" : "#DC2804"; // Can differentiate if needed

  return (
    <div className="deposit-form">
      {(isLoading||configLoading) && <ALoader />}

      <ChequeDetails
        psid={row.psid}
        chequeNumber={row.referenceNumber}
        chequeAmount={row.amountCollected}
        chequeDate={getFormatedDate(row.paymodeDate)}
      />

      <div className="mt-4">
        {/* Error Display */}
        {!validation.isValid && (
          <div
            className="error-banner mb-3"
            style={{ color: "red", fontSize: "12px", fontWeight: "bold" }}
          >
            {validation.error}
          </div>
        )}

        <AInput
          value={mode === "BOUNCE" ? "Bounce" : "Cancel/Return"}
          label="Action Type"
          style={{ height: inputHeight, borderRadius: "4px" }}
          disabled={true}
        />

        <AAutoComplete
          label="Reason"
          currentValue={form.reason}
          handleChange={(value) => rowFormHandler(value, "reason")}
          items={dropdownItems}
          height={inputHeight}
          isMulti={false}
          disabled={!validation.isValid}
        />

        {isOtherSelected && (
          <AInput
            value={form.remarks}
            label="Remarks"
            placeholder="Enter remarks (Mandatory)"
            style={{ height: inputHeight, borderRadius: "4px" }}
            handler={(value) => rowFormHandler(value, "remarks")}
          />
        )}
      </div>

      <div className="mt-3">
        <AButton
          className="no-ml-btn"
          variant="primary_filled"
          updatedStyle={{
            width: "100%",
            background: validation.isValid ? primaryColor : "#ccc",
          }}
          disabled={
            !validation.isValid || studentLoading ||configLoading ||
            !form.reason ||
            (isOtherSelected && !form.remarks)
          }
          onClick={handleConfirm}
        >
          Confirm {mode === "BOUNCE" ? "Bounce" : "Cancel/Return"}
        </AButton>
      </div>
    </div>
  );
};

export default CancelChequeForm;
